import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentElement } from '@stripe/react-stripe-js';
import PaymentIntent from '@components/stripe/stripeConnection';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Modal } from '@constants/Themed';
import { View, ScrollView, Platform, Dimensions, Image, Pressable, Alert } from 'react-native';
import Button from '@components/UI/Button/Button';
import ProductDetails from './ProductDetails';
import { set } from 'lodash';
import Checkbox from 'expo-checkbox';
import { Text } from '@constants/Themed';
import WebView from 'react-native-webview';
import ErrorBoundary from '@utils/ErrorBoundary';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';

const WebBilling = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Product Details, 2: Payment, 3: Confirmation
  const theme = themeSelector();
  const user = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

  useImperativeHandle(ref, () => ({
    openModal: (item) => {
      setItem(item);
      setVisible(true);
      setStep(1);
      setLoading(false);
    }
  }));

  const handleClose = () => {
    if (step === 2) {
      Alert.alert(
        'Cancel Payment',
        'Are you sure you want to cancel? Your payment information will be lost.',
        [
          { text: 'Continue Payment', style: 'cancel' },
          { text: 'Cancel', style: 'destructive', onPress: () => setVisible(false) }
        ]
      );
    } else {
      setVisible(false);
    }
  };

  const formatPrice = (amount: number | null, currency: string) => {
    if (amount === null || amount === undefined) {
      return 'Contact Sales';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getPlanFeatures = (product: any) => {
    return product?.marketing_features?.length > 0
      ? product.marketing_features
      : product?.features || [];
  };

  return item && (
    <Modal 
      isVisible={visible}
      setVisible={setVisible}
      animationIn="fadeInUp"
      animationOut="fadeOutDown"
      animationInTiming={500}
      animationOutTiming={300}
      hasBackdrop={true}
      backdropColor="rgba(0,0,0,0.8)"
      backdropOpacity={0.8}
      avoidKeyboard={true}
      backdropTransitionInTiming={300}
      backdropTransitionOutTiming={300}
      useNativeDriver={true}
      hideModalContentWhileAnimating={false}
      propagateSwipe={false}
      coverScreen={true}
      deviceWidth={screenWidth}
      deviceHeight={screenHeight}
      supportedOrientations={['portrait', 'landscape']}
      onBackdropPress={handleClose}
      onBackButtonPress={handleClose}
      hideCloseButton
      style={{
        margin: 0,
        padding: 0,
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Animatable.View
        animation="fadeInUp"
        duration={500}
        style={{
          width: Math.min(screenWidth * 0.95, 1000),
          height: Math.min(screenHeight * 0.9, 800), // Fixed height constraint
          maxHeight: screenHeight * 0.9, // 90vh constraint
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 24,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 20 },
          shadowOpacity: theme ? 0.15 : 0.4,
          shadowRadius: 40,
          elevation: 20,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
          flexDirection: 'column' // Ensure proper flex layout
        }}
      >
        {/* Header */}
        <View style={{
          paddingHorizontal: 24,
          paddingVertical: 20,
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e2e8f0' : '#334155',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme ? '#f8fafc' : '#0f172a',
          flexShrink: 0 // Prevent header from shrinking
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <View style={{
              backgroundColor: theme ? '#ddd6fe' : '#312e81',
              borderRadius: 12,
              padding: 8,
              marginRight: 12
            }}>
              <MaterialIcons
                name="credit-card"
                size={20}
                color={theme ? '#7c3aed' : '#a78bfa'}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{
                color: theme ? '#1e293b' : '#f1f5f9',
                fontSize: 18,
                fontWeight: '700',
                marginBottom: 2
              }}>
                Complete Your Purchase
              </Text>
              <Text style={{
                color: theme ? '#64748b' : '#94a3b8',
                fontSize: 14
              }}>
                Secure checkout powered by Stripe
              </Text>
            </View>
          </View>

          <Pressable
            onPress={handleClose}
            style={({
              backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: 8,
              alignItems: 'center',
              justifyContent: 'center'
            })}
          >
            <MaterialIcons
              name="close"
              size={20}
              color={theme ? '#64748b' : '#94a3b8'}
            />
          </Pressable>
        </View>

        {/* Progress Steps */}
        <View style={{
          paddingHorizontal: 24,
          paddingVertical: 16,
          backgroundColor: theme ? '#f1f5f9' : '#0f172a',
          flexShrink: 0 // Prevent progress section from shrinking
        }}>
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {[1, 2, 3].map((stepNumber, index) => (
              <React.Fragment key={stepNumber}>
                <View style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: step >= stepNumber ? '#3b82f6' : theme ? '#e2e8f0' : '#334155',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 2,
                  borderColor: step >= stepNumber ? '#3b82f6' : theme ? '#e2e8f0' : '#334155'
                }}>
                  <Text style={{
                    color: step >= stepNumber ? 'white' : theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: '600'
                  }}>
                    {stepNumber}
                  </Text>
                </View>
                {index < 2 && (
                  <View style={{
                    width: 40,
                    height: 2,
                    backgroundColor: step > stepNumber ? '#3b82f6' : theme ? '#e2e8f0' : '#334155',
                    marginHorizontal: 8
                  }} />
                )}
              </React.Fragment>
            ))}
          </View>
          <Text style={{
            textAlign: 'center',
            marginTop: 8,
            color: theme ? '#64748b' : '#94a3b8',
            fontSize: 12
          }}>
            {step === 1 && 'Product Details'}
            {step === 2 && 'Payment Information'}
            {step === 3 && 'Confirmation'}
          </Text>
        </View>

        {/* Content */}
        <ScrollView
          style={{
            flex: 1,
            minHeight: 0, // Allow shrinking
            maxHeight: screenHeight * 0.5 // Reduced to 50% to leave room for footer
          }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: 20 // Add bottom padding
          }}
          showsVerticalScrollIndicator={true}
        >
          <View style={{
            padding: 24,
            flex: 1,
            minHeight: 0 // Allow content to shrink
          }}>
            {step === 1 && (
              <Animatable.View
                animation="fadeInRight"
                duration={300}
                style={{ flex: 1 }}
              >
                {/* Product Summary */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#0f172a',
                  borderRadius: 16,
                  padding: 20,
                  marginBottom: 24,
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#334155',
                  maxHeight: screenHeight * 0.3, // Limit product summary height
                  overflow: 'hidden'
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                    {item?.images?.[0] && (
                      <View style={{
                        width: 60,
                        height: 60,
                        borderRadius: 12,
                        marginRight: 16,
                        overflow: 'hidden'
                      }}>
                        <Image
                          source={{ uri: item.images[0] }}
                          style={{ width: 60, height: 60 }}
                          resizeMode="cover"
                        />
                      </View>
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: 20,
                        fontWeight: '700',
                        marginBottom: 4
                      }}>
                        {item?.name}
                      </Text>
                      <Text style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 16
                      }}>
                        {formatPrice(item?.default_price?.unit_amount, item?.default_price?.currency)} / {item?.default_price?.recurring?.interval}
                      </Text>
                    </View>
                  </View>

                  {item?.description && (
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14,
                      lineHeight: 20,
                      marginBottom: 16
                    }}>
                      {item.description}
                    </Text>
                  )}

                  {/* Features */}
                  {getPlanFeatures(item).length > 0 && (
                    <View style={{ maxHeight: 200, overflow: 'hidden' }}>
                      <Text style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: 16,
                        fontWeight: '600',
                        marginBottom: 12
                      }}>
                        What's included:
                      </Text>
                      <ScrollView
                        style={{ maxHeight: 150 }}
                        showsVerticalScrollIndicator={true}
                      >
                        <View style={{ gap: 8 }}>
                          {getPlanFeatures(item).slice(0, 5).map((feature: any, index: number) => (
                          <View key={index} style={{
                            flexDirection: 'row',
                            alignItems: 'center'
                          }}>
                            <View style={{
                              width: 20,
                              height: 20,
                              borderRadius: 10,
                              backgroundColor: '#22c55e',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: 12
                            }}>
                              <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>✓</Text>
                            </View>
                            <Text style={{
                              color: theme ? '#475569' : '#cbd5e1',
                              fontSize: 14,
                              flex: 1
                            }}>
                              {feature.name}
                            </Text>
                          </View>
                        ))}
                          {getPlanFeatures(item).length > 5 && (
                            <Text style={{
                              color: theme ? '#94a3b8' : '#64748b',
                              fontSize: 12,
                              fontStyle: 'italic',
                              marginLeft: 32
                            }}>
                              +{getPlanFeatures(item).length - 5} more features
                            </Text>
                          )}
                        </View>
                      </ScrollView>
                    </View>
                  )}
                </View>

                {/* Security Badges */}
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                  marginBottom: 24
                }}>
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme ? '#f0fdf4' : '#064e3b',
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}>
                    <MaterialIcons name="security" size={16} color="#22c55e" />
                    <Text style={{
                      color: '#22c55e',
                      fontSize: 12,
                      fontWeight: '600',
                      marginLeft: 4
                    }}>
                      SSL Secured
                    </Text>
                  </View>
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}>
                    <MaterialIcons name="verified" size={16} color="#3b82f6" />
                    <Text style={{
                      color: '#3b82f6',
                      fontSize: 12,
                      fontWeight: '600',
                      marginLeft: 4
                    }}>
                      Stripe Verified
                    </Text>
                  </View>
                </View>
              </Animatable.View>
            )}

            {step === 2 && (
              <Animatable.View
                animation="fadeInRight"
                duration={300}
                style={{ flex: 1 }}
              >
                <ErrorBoundary>
                  <PaymentIntent item={item?.default_price?.id} />
                </ErrorBoundary>
              </Animatable.View>
            )}

            {step === 3 && (
              <Animatable.View
                animation="fadeInRight"
                duration={300}
                style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingVertical: 40
                }}
              >
                <View style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  backgroundColor: '#22c55e',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24
                }}>
                  <MaterialIcons name="check" size={40} color="white" />
                </View>
                <Text style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 24,
                  fontWeight: '700',
                  marginBottom: 8,
                  textAlign: 'center'
                }}>
                  Payment Successful!
                </Text>
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 16,
                  textAlign: 'center',
                  marginBottom: 32
                }}>
                  Your subscription has been activated. Welcome to {item?.name}!
                </Text>
              </Animatable.View>
            )}
          </View>
        </ScrollView>

        {/* Footer Actions */}
        <View style={{
          paddingHorizontal: 24,
          paddingVertical: 20,
          borderTopWidth: 1,
          borderTopColor: theme ? '#e2e8f0' : '#334155',
          backgroundColor: theme ? '#f8fafc' : '#0f172a',
          flexDirection: 'row',
          gap: 12,
          flexShrink: 0 // Prevent footer from shrinking
        }}>
          {step > 1 && (
            <Pressable
              onPress={() => setStep(step - 1)}
              style={{
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? '#e2e8f0' : '#334155',
                backgroundColor: 'transparent',
                minWidth: 100,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text style={{
                color: theme ? '#64748b' : '#94a3b8',
                fontSize: 16,
                fontWeight: '600'
              }}>
                Back
              </Text>
            </Pressable>
          )}

          <View style={{ flex: 1 }} />

          {step === 1 && (
            <Pressable
              onPress={() => setStep(2)}
              style={{
                paddingHorizontal: 32,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: '#3b82f6',
                minWidth: 140,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text style={{
                color: 'white',
                fontSize: 16,
                fontWeight: '700'
              }}>
                Continue to Payment
              </Text>
            </Pressable>
          )}

          {step === 3 && (
            <Pressable
              onPress={() => setVisible(false)}
              style={{
                paddingHorizontal: 32,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: '#22c55e',
                minWidth: 140,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text style={{
                color: 'white',
                fontSize: 16,
                fontWeight: '700'
              }}>
                Complete
              </Text>
            </Pressable>
          )}
        </View>
      </Animatable.View>
    </Modal>
  );
});

export default WebBilling;